import { Icon } from '@/components/ui/icon';
import { Heading } from '@/components/ui/heading';
import { Button } from '@/components/ui/button';
import { Pin } from '@/components/ui/pin';
import { StepIndicator } from './step-indicator';
import { parseText } from '@/lib/core/utils';

interface PinStepperProps {
  current: number;
  total: number;
}

interface StepPinProps {
  isVerifyMode?: boolean;
  title?: string;
  description?: string;
  error?: string;
  stepper?: PinStepperProps;
  forgotPinLabel?: string;
  onForgotPin?: () => void;
  redefineLabel?: string;
  onRedefine?: () => void;
  useBiometricLabel?: string;
  onUseBiometric?: () => void;
  inputKey?: React.Key;
  pin: string;
  onChange: (value: string) => void;
  onComplete: (value: string) => void;
}

export function StepPin({
  isVerifyMode,
  title,
  description,
  error,
  stepper,
  forgotPinLabel,
  onForgotPin,
  redefineLabel,
  onRedefine,
  useBiometricLabel,
  onUseBiometric,
  inputKey,
  pin,
  onChange,
  onComplete,
}: StepPinProps) {
  const pinInput = (
    <Pin inputKey={inputKey} value={pin} onChange={onChange} onComplete={onComplete} error={error} />
  );

  if (isVerifyMode) {
    return (
      <div className="flex flex-1 flex-col justify-between pt-35 pb-8">
        <div className="flex flex-col items-center gap-12">
          <div className="flex flex-col items-center gap-10 text-center">
            {description && (
              <Heading
                as="h1"
                className="font-didot-lt-std text-jncquoi-neutral-1000 text-[50px] leading-[120%] font-normal tracking-[-2.5px] italic"
              >
                {parseText(description)}
              </Heading>
            )}
            {title && (
              <p className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[28px] leading-normal font-semibold tracking-[0.5px]">
                {parseText(title)}
              </p>
            )}
          </div>

          {pinInput}
        </div>

        <div className="flex flex-col items-center gap-3">
          {useBiometricLabel && onUseBiometric && (
            <Button
              variant="primary-solid"
              palette="dark"
              size="lg"
              className="w-full"
              onClick={onUseBiometric}
            >
              {useBiometricLabel}
            </Button>
          )}
          {forgotPinLabel && onForgotPin && (
            <button
              type="button"
              onClick={onForgotPin}
              className="font-pp-neue-montreal text-jncquoi-neutral-1000 w-full text-center text-[11px] tracking-[0.5px] uppercase"
            >
              {forgotPinLabel}
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col gap-10 pt-15 pb-2">
      {stepper && <StepIndicator current={stepper.current} total={stepper.total} palette="dark" />}

      <div className="flex flex-col items-center gap-10.5">
        <div className="flex size-20 items-center justify-center">
          <Icon name="Dialpad" width={80} height={80} className="text-jncquoi-neutral-1000" />
        </div>
        <div className="flex flex-col items-center gap-2 text-center">
          {title && (
            <Heading
              as="h1"
              className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[28px] leading-none font-semibold tracking-[0.5px]"
            >
              {parseText(title)}
            </Heading>
          )}
          {description && (
            <p className="font-pp-neue-montreal text-[16px] leading-[100%] tracking-[0.25px] text-[#444]">
              {parseText(description)}
            </p>
          )}
        </div>
      </div>

      {pinInput}

      {redefineLabel && onRedefine && (
        <button
          type="button"
          onClick={onRedefine}
          className="font-pp-neue-montreal text-jncquoi-neutral-1000 w-full text-center text-[11px] tracking-[0.5px] uppercase"
        >
          {redefineLabel}
        </button>
      )}
    </div>
  );
}
